import { useEffect } from 'react';
import { useDomainEvent, useEvent, useLazyCommand } from '@soap/modules';

declare global {
  interface Window {
    Paddle?: {
      Setup: (options: {
        token: string;
        eventCallback: (data: { name?: string }) => void;
      }) => void;
      Environment: { set: (env: string) => void };
    };
  }
}

let paddleSetupRequestStarted = false;

/**
 * Loads Paddle client config once per tab when a billing/checkout surface mounts.
 * Avoids C231 on unrelated routes (Plan, Reports, etc.).
 */
export function useEnsurePaddleSetup(): void {
  const [sendGetPaddleData] = useLazyCommand('C231v1_GetPaddleData', null);
  const triggerCheckoutCompleted = useDomainEvent({ eventName: 'checkout-completed' });
  const triggerCheckoutClosed = useDomainEvent({ eventName: 'checkout-closed' });

  useEvent({
    eventName: 'E185v1_GotPaddleData',
    onEventReceived: (event: {
      e185_PaddleClientId?: string;
      e185_PaddleEnv?: string;
    }) => {
      const clientId = event.e185_PaddleClientId;
      const env = event.e185_PaddleEnv;
      if (!clientId || !env || typeof window.Paddle === 'undefined') {
        return;
      }

      window.Paddle.Setup({
        token: clientId,
        eventCallback(data) {
          if (data.name === 'checkout.completed') {
            triggerCheckoutCompleted({});
          }
          if (data.name === 'checkout.closed') {
            triggerCheckoutClosed({});
          }
        },
      });
      window.Paddle.Environment.set(env);
    },
  });

  useEffect(() => {
    if (paddleSetupRequestStarted) {
      return;
    }
    paddleSetupRequestStarted = true;
    sendGetPaddleData({});
  }, [sendGetPaddleData]);
}
